import { currencyFormatter, formatOrderNumber } from "@utils/formatter";
import { PRODUCT_PLACEHOLDER_SRC } from "@utils/productImage";
import {
    OrderStatusType,
    OrderPaymentStatusType,
    PENDING_SETTLEMENT_COMPACT_NOTE,
    paymentFailureNote,
    type GetMeOrdersResponse,
} from "@models/order";

interface OrderCardProps {
    order: GetMeOrdersResponse;
}

// Customer-facing status verbs for the post-E6-S2 OrderStatusType values.
const STATUS_LABELS: Record<number, string> = {
    [OrderStatusType.Received]: "Received",
    [OrderStatusType.Processing]: "Processing",
    [OrderStatusType.Shipped]: "Shipped",
    [OrderStatusType.Delivered]: "Delivered",
    [OrderStatusType.Cancelled]: "Cancelled",
};


const formatLongDate = (iso: string): string =>
    new Date(iso).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });

export const OrderCard = ({ order }: OrderCardProps) => {
    const statusLabel = STATUS_LABELS[order.status] ?? "Received";
    // Defensive: an older API payload may not carry the item summaries —
    // render the card with zeroed counts and no thumbnail row rather than crash.
    const products = order.products ?? [];
    const totalItemCount = order.totalItemCount ?? 0;
    const moreItemCount = order.moreItemCount ?? 0;
    const failureNote = paymentFailureNote(order);
    const placedDate = formatLongDate(order.createdAt);
    const statusDate = order.statusChangedAt
        ? formatLongDate(order.statusChangedAt)
        : null;

    return (
        // TODO(E6-S7): the card will navigate to the order detail page — no
        // link or cursor-pointer affordance until that page exists.
        <article className="bg-base-100 border border-base-300 rounded-lg p-4 shadow-sm">
            <div className="flex items-baseline justify-between gap-3">
                <h3 className="font-bold text-primary">
                    Order{" "}
                    {order.orderNumberFormatted ??
                        formatOrderNumber(order.orderNumber)}
                </h3>
                <div className="flex flex-col items-end">
                    <span className="font-bold text-primary">
                        {totalItemCount} Items
                    </span>
                    <span className="text-sm text-neutral/70">
                        {currencyFormatter.format(order.total)}
                    </span>
                </div>
            </div>
            {/* AO-15: the status shows the day it happened (statusChangedAt),
                and the placement date has its own line, unless it is the same
                day as the status line, as for a Received order. The days are
                compared as formatted, so as the customer's local days. An API
                before audit batch 4 sends no statusChangedAt: createdAt is
                then labelled as the placement date beside the status, never
                passed off as the status's own date. */}
            {statusDate ? (
                <>
                    <p className="text-sm text-neutral/70 mt-1">
                        {statusLabel} {statusDate}
                    </p>
                    {placedDate !== statusDate && (
                        <p className="text-sm text-neutral/70">
                            Placed {placedDate}
                        </p>
                    )}
                </>
            ) : (
                <p className="text-sm text-neutral/70 mt-1">
                    {statusLabel} · Placed {placedDate}
                </p>
            )}
            {/* §1.11: an ACH order holds until the bank settles — the compact note
                explains why the status isn't moving. Absent/other statuses (incl.
                pre-#296 payloads without the field) render nothing. */}
            {order.paymentStatus ===
                OrderPaymentStatusType.PendingSettlement && (
                <p className="text-sm text-neutral/70 mt-1">
                    {PENDING_SETTLEMENT_COMPACT_NOTE}
                </p>
            )}
            {/* #445 / audit C-11: a failed payment says so (returned, or didn't
                go through). No link to the Payment Returned page: a returned
                order stays returned after the hold lifts. */}
            {failureNote && (
                <p className="text-sm text-error mt-1">{failureNote}</p>
            )}
            {products.length > 0 && (
                <div className="flex items-center gap-4 mt-4">
                    {/* Index keys are safe here: the row is a static, never-
                        reordered slice — and productId alone can collide when
                        one product appears on multiple lines (variants). */}
                    {products.map((product, index) => (
                        <div key={index} className="relative">
                            <img
                                src={product.imageUrl ?? PRODUCT_PLACEHOLDER_SRC}
                                alt={product.name}
                                className="w-14 h-14 rounded-md object-cover"
                            />
                            <span className="absolute -top-2 -right-2 min-w-6 h-6 px-1 flex items-center justify-center rounded-full bg-base-100 border border-base-300 text-xs text-neutral shadow-sm">
                                {product.quantity}
                            </span>
                        </div>
                    ))}
                    {moreItemCount > 0 && (
                        <span className="ml-auto text-sm text-neutral">
                            +{moreItemCount} More
                        </span>
                    )}
                </div>
            )}
        </article>
    );
};
